// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// useAccess.ts - who am I and what may I open. Loads GET /api/t2/security/me once (React Query, key SECURITY_ME_KEY) and
// answers hasAccess(areaKey, action='view') against me.access (Grants: area key -> action keys).
//   Area keys: settings sections 's:<Area>:<Section>' (e.g. 's:AI Agent:Handoff'), 'phone', 'texts', 'contacts',
//   security 'sec_platform' / 'sec_users' / 'sec_roles' (shown as "User groups").
// Screens only HIDE what a person cannot open - the server refuses every call without the right (403) regardless.
//
// While /me is loading, or if it fails (e.g. an older API without the endpoint), hasAccess answers TRUE ("unknown -> show"):
// hiding everything on a failed lookup would lock a practice out of its own Settings, and the server still enforces.
//
// The last /me is mirrored in a module cache so code outside React (the Confirm-it's-you modal, the Your-account drawer that
// mounts in its own root) can read the person's 2-step methods and the confirm window without a provider.
import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { normalizePolicy, securityApi, type Grants, type SecurityMe } from './securityApi';

export const SECURITY_ME_KEY = ['t2-security-me'];

let cachedMe: SecurityMe | null = null;
let cacheState: 'unknown' | 'loaded' | 'failed' = 'unknown';

/** The last loaded /me (null until loaded). */
export function getCachedMe(): SecurityMe | null { return cachedMe; }
export function setCachedMe(me: SecurityMe | null): void {
  cachedMe = me ? { ...me, policy: normalizePolicy(me.policy) } : null;
  cacheState = me ? 'loaded' : cacheState;
}

/** Pure check against a Grants map. */
export function grantsAllow(access: Grants | null | undefined, areaKey: string, action = 'view'): boolean {
  if (!access) return false;
  const acts = access[areaKey];
  return Array.isArray(acts) && acts.includes(action);
}

/** Module-level check (outside React). Unknown (not loaded / failed) -> true; the server still enforces. */
export function hasAccess(areaKey: string, action = 'view'): boolean {
  if (cacheState !== 'loaded' || !cachedMe) return true;
  return grantsAllow(cachedMe.access, areaKey, action);
}

// ── tiny change bus: anything that changes "me" or the users list fires it; listeners refetch ──────────────
export const SECURITY_CHANGED_EVENT = 'vs:security-changed';
export function notifySecurityChanged(): void {
  try { window.dispatchEvent(new CustomEvent(SECURITY_CHANGED_EVENT)); } catch { /* old browsers */ }
}

export interface AccessApi {
  me: SecurityMe | null;
  /** true once /me answered (or failed) - before that every hasAccess is true */
  ready: boolean;
  failed: boolean;
  hasAccess: (areaKey: string, action?: string) => boolean;
  /** any action at all on the area (used to decide whether a page / menu shows) */
  canOpen: (areaKey: string) => boolean;
  refetch: () => void;
}

export function useAccess(): AccessApi {
  const qc = useQueryClient();
  const q = useQuery<SecurityMe>({
    queryKey: SECURITY_ME_KEY,
    queryFn: async () => {
      try {
        const me = await securityApi.me();
        setCachedMe(me);
        return { ...me, policy: normalizePolicy(me.policy) };
      } catch (e) {
        cacheState = 'failed';
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    const on = () => { qc.invalidateQueries({ queryKey: SECURITY_ME_KEY }); };
    window.addEventListener(SECURITY_CHANGED_EVENT, on);
    return () => window.removeEventListener(SECURITY_CHANGED_EVENT, on);
  }, [qc]);

  const me = q.data ?? null;
  const known = !!me;
  const check = useCallback((areaKey: string, action = 'view') => (known ? grantsAllow(me!.access, areaKey, action) : true), [known, me]);
  const canOpen = useCallback((areaKey: string) => (known ? (me!.access[areaKey] ?? []).length > 0 : true), [known, me]);
  const refetch = useCallback(() => { q.refetch(); }, [q]);

  return { me, ready: !q.isLoading, failed: q.isError, hasAccess: check, canOpen, refetch };
}

export default useAccess;
